import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
  DestroyRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

import { ProfileSidebarComponent } from '../../../../components/profile-sidebar/profile-sidebar.component';
import { EventHeroComponent } from '../../../../components/event-hero/event-hero.component';
import { TicketLotsComponent } from '../../../../components/ticket-lots/ticket-lots.component';
import { EventAttendeesComponent } from '../../../../components/event-attendees/event-attendees.component';
import { EventAgendaComponent } from '../../../../components/event-agenda/event-agenda.component';
import {
  EventDetail,
  EventDetailApiContent,
  CreatePaymentSuccessResponse,
  EventDetailApiTestimonial,
  EventDetailApiImage,
} from '../../../../../core/models/event.models';
import { HeaderComponent } from '../../../../components/header/header.component';
import { FooterComponent } from '../../../../components/footer/footer.component';
import { SeoService } from '../../../../../core/services/seo.service';
import { EventService } from '../../../../../core/services/event.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { environment } from '../../../../../../environments/environment';
import {
  mapEventDetailApiToViewModel,
  memberAvatarUrl,
} from '../../../../../core/utils/event-detail.mapper';

type PageState = 'loading' | 'ready' | 'error' | 'no-id';
type PaymentModalState = 'hidden' | 'loading' | 'error';

@Component({
  selector: 'app-event-detail-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ProfileSidebarComponent,
    EventHeroComponent,
    TicketLotsComponent,
    EventAgendaComponent,
    EventAttendeesComponent,
    HeaderComponent,
    FooterComponent,
  ],
  templateUrl: './event-detail-page.component.html',
})
export class EventDetailPageComponent implements OnInit, OnDestroy {
  private seoService = inject(SeoService);
  private route = inject(ActivatedRoute);
  private eventService = inject(EventService);
  private auth = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  readonly testimonialStarKeys = [1, 2, 3, 4, 5] as const;

  state: PageState = 'loading';
  errorMessage: string | null = null;
  event: EventDetail | null = null;
  /** Conteúdo bruto da API (depoimentos, imagens com legenda, totais). */
  detailContent: EventDetailApiContent | null = null;

  /** Imagem ampliada na galeria (null = popup fechado). */
  galleryLightbox: EventDetailApiImage | null = null;
  paymentModalState: PaymentModalState = 'hidden';
  paymentModalMessage = '';
  private isCreatingPayment = false;

  /** Formulário de depoimento (criação ou edição). */
  testimonialDraftText = '';
  testimonialDraftRating = 5;
  testimonialEditing = false;
  testimonialSaving = false;
  testimonialBannerError: string | null = null;

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((params) => {
          const id = params.get('id');
          if (!id) {
            this.applyNoId();
            return of(null);
          }
          this.state = 'loading';
          this.errorMessage = null;
          this.event = null;
          this.detailContent = null;
          this.isCreatingPayment = false;
          this.testimonialDraftText = '';
          this.testimonialDraftRating = 5;
          this.testimonialEditing = false;
          this.testimonialSaving = false;
          this.testimonialBannerError = null;
          this.closeGalleryLightbox(false);
          this.closePaymentModal(false);
          this.cdr.markForCheck();
          return this.eventService.getDetail(id);
        }),
      )
      .subscribe({
        next: (res) => {
          if (res === null) return;
          if (res.status !== 'SUCCESS' || !res.content) {
            this.applyError(res.message || 'Resposta inválida da API.');
            return;
          }
          this.applyDetailSuccess(res.content);
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          const msg =
            err.error?.message ??
            (typeof err.error === 'string' ? err.error : null) ??
            'Não foi possível carregar o evento.';
          this.applyError(msg);
          this.cdr.markForCheck();
        },
      });
  }

  private applyNoId(): void {
    this.state = 'no-id';
    this.errorMessage = null;
    this.event = null;
    this.detailContent = null;
    this.isCreatingPayment = false;
    this.closeGalleryLightbox(false);
    this.closePaymentModal(false);
    this.cdr.markForCheck();
  }

  private applyDetailSuccess(content: EventDetailApiContent): void {
    this.detailContent = content;
    this.event = mapEventDetailApiToViewModel(content, environment.defaultImage);
    this.state = 'ready';
    this.setupEventSEO();
  }

  private applyError(message: string): void {
    this.state = 'error';
    this.errorMessage = message;
    this.event = null;
    this.detailContent = null;
    this.isCreatingPayment = false;
    this.closeGalleryLightbox(false);
    this.closePaymentModal(false);
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.closeGalleryLightbox(false);
    this.closePaymentModal(false);
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseLightbox(): void {
    if (this.galleryLightbox) {
      this.closeGalleryLightbox();
    }
  }

  openGalleryLightbox(img: EventDetailApiImage): void {
    this.galleryLightbox = img;
    document.body.style.overflow = 'hidden';
    this.cdr.markForCheck();
  }

  /** @param mark Se true (padrão), chama `markForCheck`. */
  closeGalleryLightbox(mark = true): void {
    this.galleryLightbox = null;
    document.body.style.overflow = '';
    if (mark) this.cdr.markForCheck();
  }

  closePaymentModal(mark = true): void {
    this.paymentModalState = 'hidden';
    this.paymentModalMessage = '';
    if (mark) this.cdr.markForCheck();
  }

  get myPublicId(): string | null {
    const id = this.auth.getMemberId()?.trim();
    return id || null;
  }

  /** Depoimento do membro logado neste evento (via `event_testimonials` do get_detail). */
  get myTestimonial(): EventDetailApiTestimonial | null {
    const pid = this.myPublicId;
    if (!pid) return null;
    const list = this.detailContent?.event_testimonials ?? [];
    return list.find((t) => t.member?.public_id?.trim() === pid) ?? null;
  }

  /** Depoimentos aprovados de outros membros (evita duplicar o card \"Meu depoimento\"). */
  get approvedTestimonialsForList(): EventDetailApiTestimonial[] {
    const pid = this.myPublicId;
    return (this.detailContent?.event_testimonials ?? []).filter(
      (t) => t.is_approved && (!pid || t.member?.public_id?.trim() !== pid),
    );
  }

  /** Avatar para depoimento (API `event_testimonials[].member`). */
  testimonialAvatar(t: EventDetailApiTestimonial): string {
    return memberAvatarUrl(t.member);
  }

  get galleryImages(): EventDetailApiImage[] {
    return [...(this.detailContent?.event_images ?? [])].sort(
      (a, b) => a.position - b.position,
    );
  }

  private setupEventSEO(): void {
    if (!this.event) return;

    const cleanDescription = this.event.description
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .substring(0, 155);

    const imageUrl = this.event.bannerUrl.startsWith('http')
      ? this.event.bannerUrl
      : `${environment.siteUrl}/${this.event.bannerUrl.replace(/^\//, '')}`;

    this.seoService.updateMetaTags({
      title: this.event.title,
      description: cleanDescription,
      keywords: 'evento REDAL, networking, palestras',
      image: imageUrl,
      url: `${environment.siteUrl}/events/${this.event.id}`,
      type: 'event',
      publishedTime: this.event.dateISO,
    });

    this.seoService.setEventSchema({
      name: this.event.title,
      description: cleanDescription,
      startDate: this.event.dateISO,
      location: this.event.venue.name,
      image: imageUrl,
      url: `${environment.siteUrl}/events/${this.event.id}`,
    });

    this.seoService.setBreadcrumbSchema([
      { name: 'Início', url: environment.siteUrl },
      { name: 'Eventos', url: `${environment.siteUrl}/events` },
      { name: this.event.title, url: `${environment.siteUrl}/events/${this.event.id}` },
    ]);
  }

  onSelectLot(lot: { id: string; paymentLink?: boolean | null }): void {
    if (lot.paymentLink === true || this.isCreatingPayment || !this.event?.id) {
      return;
    }

    this.isCreatingPayment = true;
    this.paymentModalState = 'loading';
    this.paymentModalMessage = 'Estamos gerando sua cobrança. Isso pode levar alguns segundos...';
    this.cdr.markForCheck();

    this.eventService.createPayment(this.event.id).subscribe({
      next: (res: CreatePaymentSuccessResponse) => {
        const paymentUrl = res.content?.member_payment?.payment_link?.trim();
        if (!paymentUrl) {
          this.paymentModalState = 'error';
          this.paymentModalMessage = 'Não foi possível obter o link de pagamento.';
          this.isCreatingPayment = false;
          this.cdr.markForCheck();
          return;
        }

        this.closePaymentModal(false);
        this.isCreatingPayment = false;
        this.cdr.markForCheck();
        window.location.href = paymentUrl;
      },
      error: (err: HttpErrorResponse) => {
        const message =
          err.error?.message ??
          (typeof err.error === 'string' ? err.error : null) ??
          'Não foi possível iniciar a compra do ingresso.';
        this.paymentModalState = 'error';
        this.paymentModalMessage = message;
        this.isCreatingPayment = false;
        this.cdr.markForCheck();
      },
    });
  }

  /** Exibe estrelas simples (0–5) para depoimentos na lista (inteiro da API). */
  testimonialStars(rating: number): boolean[] {
    const r = Math.min(5, Math.max(0, Math.round(rating)));
    return Array.from({ length: 5 }, (_, i) => i < r);
  }

  /** Estrelas para o rascunho (valor inteiro 0–5, sem arredondar de fracionário). */
  testimonialStarsExact(rating: number): boolean[] {
    const r = Math.min(5, Math.max(0, Math.floor(rating)));
    return Array.from({ length: 5 }, (_, i) => i < r);
  }

  setTestimonialDraftRating(value: number): void {
    this.testimonialDraftRating = Math.min(5, Math.max(0, Math.round(value)));
  }

  startTestimonialEdit(): void {
    const mine = this.myTestimonial;
    if (!mine) return;
    this.testimonialEditing = true;
    this.testimonialBannerError = null;
    this.testimonialDraftText = mine.testimonial ?? '';
    this.testimonialDraftRating = Math.min(5, Math.max(0, mine.rating ?? 0));
    this.cdr.markForCheck();
  }

  cancelTestimonialEdit(): void {
    this.testimonialEditing = false;
    this.testimonialBannerError = null;
    this.cdr.markForCheck();
  }

  submitTestimonial(): void {
    const eventPublicId = this.event?.id;
    if (!eventPublicId || this.testimonialSaving) return;

    const text = this.testimonialDraftText.trim();
    if (!text) {
      this.testimonialBannerError = 'Escreva seu depoimento antes de enviar.';
      this.cdr.markForCheck();
      return;
    }

    const rating = Math.min(5, Math.max(0, Math.round(this.testimonialDraftRating)));
    const mine = this.myTestimonial;

    this.testimonialSaving = true;
    this.testimonialBannerError = null;
    this.cdr.markForCheck();

    if (mine && this.testimonialEditing) {
      this.eventService
        .updateTestimonial({ id: mine.id, testimonial: text, rating })
        .subscribe({
          next: (res) => {
            this.testimonialSaving = false;
            if (res.status !== 'SUCCESS') {
              this.testimonialBannerError = res.message || 'Não foi possível atualizar.';
              this.cdr.markForCheck();
              return;
            }
            this.testimonialEditing = false;
            this.refreshCurrentEventDetail();
          },
          error: (err: HttpErrorResponse) => {
            this.testimonialSaving = false;
            this.testimonialBannerError =
              err.error?.message ??
              (typeof err.error === 'string' ? err.error : null) ??
              'Não foi possível atualizar o depoimento.';
            this.cdr.markForCheck();
          },
        });
      return;
    }

    this.eventService
      .createTestimonial({ event_id: eventPublicId, testimonial: text, rating })
      .subscribe({
        next: (res) => {
          this.testimonialSaving = false;
          if (res.status !== 'SUCCESS') {
            this.testimonialBannerError = res.message || 'Não foi possível enviar.';
            this.cdr.markForCheck();
            return;
          }
          this.refreshCurrentEventDetail();
        },
        error: (err: HttpErrorResponse) => {
          this.testimonialSaving = false;
          if (err.status === 409) {
            this.testimonialBannerError = null;
            this.refreshCurrentEventDetail();
            this.cdr.markForCheck();
            return;
          }
          this.testimonialBannerError =
            err.error?.message ??
            (typeof err.error === 'string' ? err.error : null) ??
            'Não foi possível enviar o depoimento.';
          this.cdr.markForCheck();
        },
      });
  }

  deleteMyTestimonial(): void {
    const mine = this.myTestimonial;
    if (!mine || this.testimonialSaving) return;
    if (
      !confirm(
        'Excluir seu depoimento? Ele deixará de aparecer para você e será removido após confirmação no servidor.',
      )
    ) {
      return;
    }

    this.testimonialSaving = true;
    this.testimonialBannerError = null;
    this.cdr.markForCheck();

    this.eventService.deleteTestimonial(mine.id).subscribe({
      next: (res) => {
        this.testimonialSaving = false;
        if (res.status !== 'SUCCESS') {
          this.testimonialBannerError = res.message || 'Não foi possível excluir.';
          this.cdr.markForCheck();
          return;
        }
        this.testimonialEditing = false;
        this.testimonialDraftText = '';
        this.testimonialDraftRating = 5;
        this.refreshCurrentEventDetail();
      },
      error: (err: HttpErrorResponse) => {
        this.testimonialSaving = false;
        this.testimonialBannerError =
          err.error?.message ??
          (typeof err.error === 'string' ? err.error : null) ??
          'Não foi possível excluir o depoimento.';
        this.cdr.markForCheck();
      },
    });
  }

  /** Data/hora do depoimento para exibição junto ao nome do autor. */
  formatTestimonialCommentAt(iso: string | null | undefined): string {
    if (!iso?.trim()) return '—';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  }

  private refreshCurrentEventDetail(): void {
    const publicId = this.event?.id ?? this.route.snapshot.paramMap.get('id');
    if (!publicId) {
      this.cdr.markForCheck();
      return;
    }
    this.eventService.getDetail(publicId).subscribe({
      next: (res) => {
        if (res.status !== 'SUCCESS' || !res.content) return;
        this.applyDetailSuccess(res.content);
        this.cdr.markForCheck();
      },
      error: () => {
        this.cdr.markForCheck();
      },
    });
  }
}
